import { Dispatch, RefObject, SetStateAction, useCallback, useRef } from 'react';
import ReactPlayer from 'react-player';

import { TPresenceData } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { BoxRow } from '@/features/broadcast-schedule-program/types';
import { ReactPlayerWithWrapper } from '@/types/video';
import { convertDatesToSeconds } from '@/utils/hh-mm-ss-into-number';

import { OnProgressType } from '../VideoPlayer';

import { VideoPlayerActions } from './useVideoPlayerState';

export interface VideoPlayerMainHandlers {
  handlePlayPause: () => void;
  handlePlay: () => void;
  handlePause: () => void;
  handleProgress: (state: OnProgressType) => void;
  handleDuration: (dur: number) => void;
}
export interface VideoPlayerHandlers extends VideoPlayerMainHandlers {
  handleEnded: () => void;
  handleSeek: (value: number) => void;
  handleVolumeChange: (value: number) => void;
  handlePlaybackRateChange: (value: number) => void;
  handleFullscreen: () => void;
  handleReady: () => void;
  handleError: (error: unknown) => void;
  handleChangeWidth: (value: '20%' | '50%' | '100%') => void;
}

export function useVideoPlayerHandlers(
  state: {
    isPlaying: boolean;
    played: number;
    duration: number;
    currentVolume: number;
    videoHasError: boolean;
    isVideoLoading: boolean;
  },
  actions: VideoPlayerActions,
  playerRef: RefObject<ReactPlayer>,
  options: {
    drawerLogo?: boolean;
    hasError?: boolean;
    isLoading?: boolean;
    pendingSeekTime?: number | null;
    onPlay?: () => void;
    onPause?: () => void;
    onProgress?: (state: OnProgressType) => void;
    onEnded?: () => void;
    onEndPresence?: (handlers: VideoPlayerMainHandlers) => void;
    setActiveBox?: Dispatch<SetStateAction<BoxRow | null>>;
    activeBox?: BoxRow | null;
    presenceData?: TPresenceData;
    setPlayerVideoCurrentTime?: Dispatch<SetStateAction<number>>;
    setPlayerVideoDuration?: Dispatch<SetStateAction<number>>;
    setPendingSeekTime?: Dispatch<SetStateAction<number | null>>;
    isTooltipActive?: boolean;
    progressStateRef?: RefObject<{ played: number; playedSeconds: number }>;
    lastProgressRef?: RefObject<number>;
    stopRewind?: () => void;
    checkProgress?: (playedSeconds: number, handlers: VideoPlayerMainHandlers) => void;
    checkSeek?: (seekedSeconds: number) => void;
  }
): VideoPlayerHandlers {
  const {
    drawerLogo,
    hasError,
    isLoading,
    pendingSeekTime,
    onPlay,
    onPause,
    onProgress,
    onEnded,
    onEndPresence,
    activeBox,
    presenceData,
    setActiveBox,
    setPlayerVideoCurrentTime,
    setPlayerVideoDuration,
    setPendingSeekTime,
    stopRewind,
    checkProgress,
    checkSeek,
  } = options;

  const checkProgressRef = useRef(checkProgress);
  checkProgressRef.current = checkProgress;

  const onEndPresenceRef = useRef(onEndPresence);
  onEndPresenceRef.current = onEndPresence;

  // Refs to always capture the latest activeBox and presenceData values inside
  // checkPresence/checkNextTrigger, which are plain functions (not useCallback) and
  // therefore would otherwise capture stale closure values from the render in which
  // they were created.  After clearPresence() + goToPresence() React batches state
  // updates, so the very next onProgress tick may still see the old values — refs
  // solve this by giving us a mutable cell that is updated synchronously.
  const activeBoxRef = useRef(activeBox);
  activeBoxRef.current = activeBox;

  const presenceDataRef = useRef(presenceData);
  presenceDataRef.current = presenceData;

  // Set to true by handleSeek so that the very next onProgress tick — which may
  // fire before React has committed the new state from checkSeek/clearPresence/
  // goToPresence — skips checkPresence and checkNextTrigger.  Both functions read
  // activeBoxRef and presenceDataRef which are only updated on render; running them
  // with stale refs causes spurious setActiveBox(null) calls and wrong presence
  // highlights, producing the visible border flicker.
  const isSeekingRef = useRef(false);

  const handlePlayPause = useCallback(() => {
    if (drawerLogo) {
      actions.setIsPlaying(false);
      onPause?.();
      return;
    }
    if (stopRewind) {
      stopRewind();
    }
    actions.setIsPlaying(!state.isPlaying);
    if (state.isPlaying && onPause) {
      onPause();
    } else if (!state.isPlaying && onPlay) {
      onPlay();
    }
  }, [drawerLogo, state.isPlaying, onPause, onPlay, setActiveBox, actions, stopRewind]);

  const handlePlay = useCallback(() => {
    if (stopRewind) {
      stopRewind();
    }
    actions.setIsPlaying(true);
    if (onPlay) onPlay();
  }, [onPlay, setActiveBox, actions, stopRewind]);

  const handlePause = useCallback(() => {
    actions.setIsPlaying(false);
    if (onPause) onPause();
  }, [onPause, setActiveBox, actions]);

  const checkPresence = (progressState: OnProgressType) => {
    // Use refs so we always read the latest values, even if React hasn't re-rendered
    // yet after a clearPresence() + goToPresence() call during a seek.
    const currentPresenceData = presenceDataRef.current;
    const currentActiveBox = activeBoxRef.current;

    const endPresenceSeconds = currentPresenceData?.end_datetime
      ? Math.abs(
          convertDatesToSeconds(
            currentPresenceData?.start_datetime,
            currentPresenceData?.end_datetime
          )
        )
      : 0;
    const relativePlayedSeconds = progressState.playedSeconds - (currentActiveBox?.frame || 0);
    const current = playerRef.current as ReactPlayerWithWrapper | null;
    const wrapper = current?.wrapper as HTMLDivElement;
    const isPlayingPresence =
      (currentActiveBox &&
        state.isPlaying &&
        progressState.playedSeconds > 0 &&
        progressState.playedSeconds > currentActiveBox.frame &&
        relativePlayedSeconds < endPresenceSeconds) ||
      false;
    wrapper?.classList.toggle('active', isPlayingPresence);

    if (currentActiveBox && relativePlayedSeconds > endPresenceSeconds) {
      setActiveBox?.(null);
      onEndPresenceRef.current?.({
        handlePlayPause,
        handlePlay,
        handlePause,
        handleProgress,
        handleDuration,
      });
    }
  };

  const checkNextTrigger = (progressState: OnProgressType) => {
    // Read from ref so we see the latest activeBox value, not the closure-captured one.
    if (activeBoxRef.current || !checkProgressRef.current) {
      return;
    }

    checkProgressRef.current(progressState.playedSeconds, {
      handlePlayPause,
      handlePlay,
      handlePause,
      handleProgress,
      handleDuration,
    });
  };

  const handleProgress = useCallback(
    (progressState: OnProgressType) => {
      actions.setPlayed(progressState.played);

      if (isSeekingRef.current) {
        // Skip presence checks this tick: refs are stale until React re-renders
        // after the seek-triggered state updates (clearPresence + goToPresence).
        // Running checkPresence with a stale activeBoxRef would call setActiveBox(null)
        // and undo the correct highlight, causing the border flicker.
        isSeekingRef.current = false;
      } else {
        checkPresence(progressState);
        checkNextTrigger(progressState);
      }

      setPlayerVideoCurrentTime?.(progressState.playedSeconds);

      if (onProgress) {
        onProgress(progressState);
      }
    },
    [onProgress, setPlayerVideoCurrentTime, actions]
  );

  const handleEnded = useCallback(() => {
    actions.setIsPlaying(false);
    if (onEnded) onEnded();
  }, [onEnded, actions]);

  const handleDuration = useCallback(
    (dur: number) => {
      actions.setDuration(dur);
      setPlayerVideoDuration?.(dur);
    },
    [setPlayerVideoDuration, actions]
  );

  const handleSeek = useCallback(
    (value: number) => {
      isSeekingRef.current = true;
      actions.setPlayed(value);
      if (playerRef.current) {
        playerRef.current.seekTo(value, 'fraction');
      }
      if (checkSeek && state.duration > 0) {
        checkSeek(value * state.duration);
      }
    },
    [playerRef, actions, checkSeek, state.duration]
  );

  const handleVolumeChange = useCallback(
    (value: number) => {
      actions.setCurrentVolume(value);
    },
    [actions]
  );

  const handlePlaybackRateChange = useCallback(
    (value: number) => {
      actions.setCurrentRate(value);
    },
    [actions]
  );

  const handleFullscreen = useCallback(() => {
    if (hasError || isLoading || state.videoHasError || state.isVideoLoading) return;
    const html5Video = playerRef.current?.getInternalPlayer();
    if (html5Video?.requestFullscreen) {
      html5Video.requestFullscreen();
    }
  }, [hasError, isLoading, state.videoHasError, state.isVideoLoading, playerRef]);

  const handleReady = useCallback(() => {
    actions.setIsVideoLoading(false);
    if (pendingSeekTime) {
      playerRef.current?.seekTo(pendingSeekTime, 'seconds');
      setPendingSeekTime?.(null);
    }
  }, [pendingSeekTime, setPendingSeekTime, playerRef, actions]);

  const isAutoplayError = (err: unknown): boolean => {
    const anyErr = err as any;
    const name = anyErr?.name as string | undefined;
    const message: string | undefined =
      typeof err === 'string'
        ? err
        : anyErr?.message ||
          (typeof anyErr?.toString === 'function' ? anyErr.toString() : undefined);

    return (
      name === 'NotAllowedError' ||
      name === 'AbortError' ||
      (!!message &&
        (/NotAllowedError/i.test(message) ||
          /play\(\) request was interrupted/i.test(message) ||
          /without user interaction/i.test(message) ||
          /user (?:hasn'?t|didn'?t) interact/i.test(message)))
    );
  };

  const handleError = useCallback(
    (error: unknown) => {
      console.error('Error loading video:', error);

      if (isAutoplayError(error)) {
        actions.setIsPlaying(false);
        actions.setIsVideoLoading(false);
        actions.setVideoHasError(false);
        return;
      }

      actions.setVideoHasError(true);
      actions.setIsVideoLoading(false);
    },
    [actions]
  );

  const handleChangeWidth = useCallback(
    (value: '20%' | '50%' | '100%') => {
      actions.setActualWidth(value);
    },
    [actions]
  );

  return {
    handlePlayPause,
    handlePlay,
    handlePause,
    handleProgress,
    handleEnded,
    handleDuration,
    handleSeek,
    handleVolumeChange,
    handlePlaybackRateChange,
    handleFullscreen,
    handleReady,
    handleError,
    handleChangeWidth,
  };
}
